import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CTASection from '@/pages/home/components/CTASection';
import { articles, hotRanking, comments } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import {
  OrganizationSchema,
  WebSiteSchema,
  WebPageSchema,
  BreadcrumbListSchema,
  ArticleSchema,
} from '@/components/feature/SchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.slug === slug) || fallbackArticle;
  const canonicalUrl = typeof window !== 'undefined' ? 'http://sxthpxzx.cn' : '';

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">文章未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleUrl = `${canonicalUrl}/article/${article.slug}`;
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        title={`${article.title} - ${siteInfo.name}`}
        description={article.excerpt}
        url={articleUrl}
        datePublished={article.date}
      />
      <ArticleSchema
        headline={article.title}
        description={article.excerpt}
        url={articleUrl}
        image={article.image}
        datePublished={article.date}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: canonicalUrl },
          { name: article.categoryName, url: `${canonicalUrl}/category/${article.category}` },
          { name: article.title, url: articleUrl },
        ]}
      />
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="w-full bg-background-50 pt-20 pb-4">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/category/${article.category}`} className="hover:text-primary-500 transition-colors cursor-pointer">{article.categoryName}</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
              </nav>
            </div>
          </div>
        </div>

        {/* Article Hero */}
        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/25 to-black/70"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <div className="flex flex-col lg:flex-row items-start gap-8">
                <div className="w-full lg:w-5/12 pt-8">
                  <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-3 whitespace-nowrap">
                    {article.categoryName}
                  </span>
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 leading-tight">
                    {article.title}
                  </h1>
                  <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-4 mt-4">
                    <span className="px-5 py-2 bg-background-50/15 text-background-50 text-sm font-medium rounded-full cursor-pointer hover:bg-background-50/25 transition-colors whitespace-nowrap">
                      <i className="ri-bookmark-line mr-1.5"></i>收藏
                    </span>
                    <span className="px-5 py-2 bg-background-50/15 text-background-50 text-sm font-medium rounded-full cursor-pointer hover:bg-background-50/25 transition-colors whitespace-nowrap">
                      <i className="ri-share-forward-line mr-1.5"></i>分享
                    </span>
                  </div>
                </div>
                <div className="w-full lg:w-7/12"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content + Sidebar */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                {/* Main Content */}
                <article className="w-full lg:w-[65%]">
                  <div className="flex items-center gap-4 mb-6 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(1)}万 阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line"></i> {article.comments} 评论
                    </span>
                  </div>

                  <div className="prose prose-lg max-w-none text-foreground-800 leading-relaxed">
                    <p className="text-base md:text-lg leading-loose">
                      {article.excerpt}
                    </p>
                    <p className="mt-4 text-base leading-loose">
                      据知情人士透露，该事件在圈内早已不是秘密。多位不愿具名的圈内人士表示，类似的情况在娱乐圈和网红圈中相当普遍，
                      只是大多数时候被资本和公关团队压了下来没有曝光。这次的爆料之所以能引起如此大的反响，很大程度上是因为涉事方此前一直以正面形象示人，
                      与真实情况形成了强烈反差。
                    </p>
                    <p className="mt-4 text-base leading-loose">
                      事件发酵后，涉事方尚未对此作出官方回应。但其社交媒体账号评论区已被网友攻陷，
                      大量粉丝表示"滤镜碎了""没想到你是这样的人"，也有部分忠实粉丝选择继续支持，认为"谁还没有个过去"。
                    </p>
                    <p className="mt-4 text-base leading-loose">
                      随着事件的持续发酵，越来越多的相关爆料浮出水面。
                      有网友翻出了该事件相关方早期的言论和行径，与此前树立的人设形成了鲜明对比。
                      这种"人设崩塌"式的爆料，再次引发了公众对于网红明星真实性的讨论。
                    </p>
                    <p className="mt-4 text-base leading-loose">
                      业内人士分析认为，这一事件的持续发酵可能会对整个行业产生深远影响。
                      首先是商业层面，涉事方目前合作的多个品牌已经开始评估是否继续合作，据估算涉及金额可能高达数千万元。
                      其次是粉丝层面，大规模的脱粉现象已经出现，相关社交平台的话题阅读量在短时间内突破了数亿次。
                    </p>
                    <p className="mt-4 text-base leading-loose">
                      吃瓜群将持续关注此事件的后续发展，为吃瓜群众带来最新、最全的跟踪报道。
                      同时也提醒广大吃瓜群众，理性吃瓜，不信谣不传谣，等待更多实锤证据的出现。
                    </p>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-background-200">
                    <span className="text-xs text-foreground-500">标签：</span>
                    <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">{article.categoryName}</span>
                    <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">爆料</span>
                    <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">吃瓜</span>
                    <span className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">实锤</span>
                  </div>
                </article>

                {/* Sidebar */}
                <aside className="w-full lg:w-[35%]">
                  {/* Hot Ranking */}
                  <div className="bg-background-100 rounded-2xl p-5 md:p-6 mb-6">
                    <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                      热门黑料 🔥 实时排行
                    </h3>
                    <ul className="space-y-0">
                      {hotRanking.map((item) => (
                        <li key={item.rank}>
                          <Link
                            to={`/article/${item.slug}`}
                            className="flex items-center gap-3 py-2.5 border-b border-background-200/60 last:border-0 hover:bg-background-50/50 rounded-lg px-2 -mx-2 transition-colors cursor-pointer"
                          >
                            <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                              item.rank <= 3
                                ? 'bg-primary-500 text-background-50'
                                : 'bg-background-200 text-foreground-600'
                            }`}>
                              {item.rank}
                            </span>
                            <span className="text-sm text-foreground-800 line-clamp-1 flex-1">{item.title}</span>
                            <span className="text-xs text-foreground-500 flex-shrink-0">{item.views}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/"
                      className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
                    >
                      查看更多排行 <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                  </div>

                  {/* QQ Group CTA */}
                  <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl p-5 md:p-6 text-background-50">
                    <h3 className="text-lg font-heading font-bold mb-2">加入QQ吃瓜群</h3>
                    <p className="text-sm text-background-50/80 leading-relaxed mb-4">
                      实时推送最新黑料，每日更新热门大瓜，不再错过任何一个爆料！
                    </p>
                    <a
                      href="http://sxthpxzx.cn/q/88888888"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-50 text-primary-600 text-sm font-semibold rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-qq-line"></i>
                      立即加群
                    </a>
                  </div>
                </aside>
              </div>
            </div>
          </div>
        </section>

        {/* Comments Section */}
        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                网友神评
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-background-50 rounded-2xl p-5 md:p-6">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-fill text-sm ${
                            i < comment.stars ? 'text-secondary-500' : 'text-background-300'
                          }`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed line-clamp-4 mb-4">
                      &ldquo;{comment.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-background-100">
                      <img
                        src={comment.avatar}
                        alt={comment.username}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{comment.username}</p>
                        <p className="text-xs text-foreground-500">{comment.date}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full py-12 md:py-16 bg-background-50">
            <div className="w-full px-4 md:px-6">
              <div className="mx-auto max-w-7xl">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                  猜你喜欢
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden transition-all duration-300 hover:border-primary-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-base font-heading font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        <CTASection />
      </main>
      <Footer />
    </>
  );
}